import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { Computer, Fingerprint } from '@mui/icons-material'
import { CopyButton, WizardHeader } from '@/common/components'
import { validateName } from '@/common/utils'
import { useHostAddition } from '@/features/host-registration'
import type { HostAddResponse } from '@/features/host-registration'
import { useUserInitializationContext } from '@/features/user'
import { useOnboardingUpdate } from '../../hooks'

interface HostSetupStepProps {
  readonly onStepComplete: () => void
}

type StepPhase = 'form' | 'display' | 'saving'

export const HostSetupStep: FC<HostSetupStepProps> = ({ onStepComplete }) => {
  const { updateOnboardingStep } = useUserInitializationContext()
  const { addHostAsync, isAdding, error: addError } = useHostAddition()
  const {
    updateStepAsync,
    isUpdating,
    error: onboardError,
    reset: resetOnboard,
  } = useOnboardingUpdate()

  const [phase, setPhase] = useState<StepPhase>('form')
  const [registeredHost, setRegisteredHost] = useState<HostAddResponse | null>(null)
  const [onboardErrorMessage, setOnboardErrorMessage] = useState<string | null>(null)

  const [hostName, setHostName] = useState('')
  const [touched, setTouched] = useState(false)

  const validation = useMemo(() => validateName(hostName, 'Host name'), [hostName])
  const isFormValid = validation.isValid

  const handleHostNameChange = useCallback((event: { target: { value: string } }) => {
    setHostName(event.target.value)
    setTouched(true)
  }, [])

  const handleRegister = useCallback(async () => {
    if (!isFormValid) return

    const result = await addHostAsync({ hostName })
    if (result.success) {
      setRegisteredHost(result.data)
      setPhase('display')
    }
  }, [isFormValid, hostName, addHostAsync])

  const handleContinue = useCallback(async () => {
    setPhase('saving')
    setOnboardErrorMessage(null)

    try {
      const result = await updateStepAsync({ hostRegistered: true })

      if (result && typeof result === 'object' && 'success' in result && result.success === false) {
        setOnboardErrorMessage('Host registered, but progress could not be saved.')
        setPhase('display')
        return
      }

      updateOnboardingStep('hostRegistered')
      onStepComplete()
    } catch {
      setOnboardErrorMessage('Host registered, but progress could not be saved.')
      setPhase('display')
    }
  }, [updateStepAsync, updateOnboardingStep, onStepComplete])

  const handleSkip = useCallback(() => {
    onStepComplete()
  }, [onStepComplete])

  const handleRetryOnboard = useCallback(() => {
    resetOnboard()
    setOnboardErrorMessage(null)
  }, [resetOnboard])

  const isDisabled = isAdding || isUpdating || phase === 'saving'
  const displayError = addError ?? onboardErrorMessage ?? onboardError

  const showDisplayView = (phase === 'display' || phase === 'saving') && registeredHost !== null

  if (showDisplayView) {
    const copyText = `Host Name: ${registeredHost.hostName}\nHost ID: ${registeredHost.hostId}`

    return (
      <Box sx={containerSx}>
        <Typography variant="h6" sx={titleSx}>
          Host Registered
        </Typography>

        <Alert severity="warning" sx={alertSx}>
          Save these details. You will need them to configure your daemon.
        </Alert>

        <Box sx={infoSx}>
          <Box sx={infoHeaderSx}>
            <CopyButton text={copyText} tooltipText="Copy host details" />
          </Box>
          <Box sx={infoRowSx}>
            <Computer sx={infoIconSx} />
            <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
              Name
            </Typography>
            <Typography variant="body1" color="text.primary">
              {registeredHost.hostName}
            </Typography>
          </Box>
          <Box sx={infoRowSx}>
            <Fingerprint sx={infoIconSx} />
            <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
              Host ID
            </Typography>
            <Typography variant="body1" color="text.primary" sx={monoSx}>
              {registeredHost.hostId}
            </Typography>
          </Box>
        </Box>

        {displayError && (
          <Alert
            severity="error"
            sx={alertSx}
            action={
              onboardErrorMessage ? (
                <Button color="inherit" size="small" onClick={handleRetryOnboard}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {displayError}
          </Alert>
        )}

        {phase === 'saving' && !displayError && (
          <Box sx={savingContainerSx}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Saving progress...
            </Typography>
          </Box>
        )}

        <Box sx={actionsCenterSx}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={isUpdating || phase === 'saving'}
            startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Continue
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <WizardHeader
        icon={<Computer />}
        title="Register Your First Host"
        description="Hosts identify the machines running your Claude Code daemons."
      />

      <Box sx={containerSx}>
        <TextField
          label="Host Name"
          value={hostName}
          onChange={handleHostNameChange}
          error={touched && !validation.isValid}
          helperText={
            touched ? validation.error : 'A descriptive name for your host (e.g., My Computer)'
          }
          fullWidth
          disabled={isDisabled}
          sx={fieldSx}
          autoFocus
        />

        {displayError && (
          <Alert severity="error" sx={alertSx}>
            {displayError}
          </Alert>
        )}

        <Box sx={actionsSx}>
          <Button onClick={handleSkip} disabled={isDisabled}>
            Skip for Now
          </Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={!isFormValid || isDisabled}
            startIcon={isAdding ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isAdding ? 'Registering...' : 'Register Host'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 2,
}

const fieldSx: SxProps<Theme> = {
  mb: 3,
}

const alertSx: SxProps<Theme> = {
  mb: 2,
}

const infoSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 1,
  bgcolor: 'action.hover',
  mb: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  position: 'relative',
}

const infoHeaderSx: SxProps<Theme> = {
  position: 'absolute',
  top: 8,
  right: 8,
}

const infoRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

const infoIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'primary.main',
}

const infoLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  minWidth: 70,
}

const monoSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  wordBreak: 'break-all',
}

const savingContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  mb: 2,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}

const actionsCenterSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 4,
}
